import { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../../Context';
import { AuthContextApi } from '../../../Context/api';
import {
    ExclamationTriangleIcon,
    ChartBarIcon,
    DocumentTextIcon,
    CalendarIcon,
    CodeBracketIcon,
    MagnifyingGlassIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    EyeIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import NavMenu from '../../../Components/NavMenu/Tecnico';


const Logs = () => {

    const { states, setters } = useContext(AuthContext)
    const { checkLogin,
        getLogsDataSistema,
        getLogs,
        getLogsMetricas
    } = useContext(AuthContextApi)

    // Estados locais para busca dinâmica e ordenação
    const [selectedSortField, setSelectedSortField] = useState('data_criacao')
    const [sortDirection, setSortDirection] = useState('desc')
    const [recordsPerPage, setRecordsPerPage] = useState(10)
    const [viewingLog, setViewingLog] = useState(null)
    const [showViewModal, setShowViewModal] = useState(false)

    // Opções de ordenação
    const sortOptions = [
        { value: 'data_criacao', label: 'Data de Criação' },
        { value: 'data_atualizacao', label: 'Data de Atualização' },
        { value: 'linhaDoErro', label: 'Linha do Erro' },
        { value: 'nomeDoArquivo', label: 'Nome do Arquivo' }
    ]

    const primeiroLoad = async () => {
        await getLogsDataSistema()
        await getLogsMetricas()
        const params = new URLSearchParams({
            page: 0,
            per_page: recordsPerPage,
            sort: selectedSortField,
            sort_dir: sortDirection
        })
        await getLogs(params.toString())
        await checkLogin()
    }

    const loadLogs = async () => {
        const params = new URLSearchParams({
            page: states.page || 0,
            per_page: recordsPerPage,
            sort: selectedSortField,
            sort_dir: sortDirection
        })
        await getLogs(params.toString())
    }

    const next = async () => {
        setters.setPage(states.page + 1)
    }

    const previous = async () => {
        if (states.page > 0) {
            setters.setPage(states.page - 1)
        }
    }

    const openViewModal = async (log) => {
        setViewingLog(log)
        setShowViewModal(true)
    }

    const closeViewModal = () => {
        setViewingLog(null)
        setShowViewModal(false)
    }

    useEffect(() => {
        primeiroLoad()

        return () => {
            setters.setDataEstatisticaLogs([])
            setters.setDataLogs([])
            setters.setLogs({})
            setters.setPage(0)
            setters.setModalLogs(false)
        }

    }, [])

    useEffect(() => {
        loadLogs()
    }, [states.page, sortDirection, selectedSortField, recordsPerPage])

    return (
        <>
            <NavMenu />
            {/* Header da Página */}
            <div className="bg-white border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex items-center">
                        <div className="p-2 rounded-lg bg-red-100 mr-3">
                            <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-semibold text-gray-900">
                                Logs do Sistema
                            </h1>
                            <p className="text-sm text-gray-600">
                                Monitoramento e análise de eventos e erros do sistema
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Conteúdo Principal */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Cards de Estatísticas - Design Artístico */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {states.dataEstatisticaLogs?.length > 0 && states.dataEstatisticaLogs.map((card, index) => (
                        <div key={card.title} className="relative bg-gradient-to-br from-red-50 via-white to-orange-50/30 backdrop-blur-sm rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-orange-500/5"></div>
                            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-red-400/10 to-orange-400/10 rounded-full -translate-y-12 translate-x-12"></div>
                            <div className="relative p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-3 rounded-2xl bg-gradient-to-r from-red-500 via-orange-600 to-red-700 shadow-lg">
                                        <ChartBarIcon className="w-6 h-6 text-white" />
                                    </div>
                                    <div className="text-right">
                                        <div className="text-2xl font-bold bg-gradient-to-r from-gray-900 via-red-800 to-orange-800 bg-clip-text text-transparent">
                                            {card.value}
                                        </div>
                                    </div>
                                </div>
                                <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 via-red-800 to-orange-700 bg-clip-text text-transparent">
                                    {card.title}
                                </h3>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Formulário de Ordenação - Design Artístico */}
                <div className="relative bg-gradient-to-br from-red-50/80 via-white/80 to-orange-50/80 backdrop-blur-sm rounded-3xl shadow-lg border border-red-100/50 mb-8 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-orange-500/5"></div>
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-red-400/10 to-orange-400/10 rounded-full -translate-y-16 translate-x-16"></div>
                    <div className="relative p-6">
                        <div className="flex items-center mb-4">
                            <div className="p-3 rounded-2xl bg-gradient-to-r from-red-500 via-orange-600 to-red-600 shadow-lg mr-4">
                                <MagnifyingGlassIcon className="w-6 h-6 text-white" />
                            </div>
                            <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-900 via-red-800 to-orange-800 bg-clip-text text-transparent">
                                Configurações de Exibição
                            </h2>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Campo de Ordenação</label>
                                <select
                                    value={selectedSortField}
                                    onChange={(e) => setSelectedSortField(e.target.value)}
                                    className="w-full px-4 py-3 border border-red-200 rounded-2xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm"
                                >
                                    {sortOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Direção da Ordenação</label>
                                <select
                                    value={sortDirection}
                                    onChange={(e) => setSortDirection(e.target.value)}
                                    className="w-full px-4 py-3 border border-red-200 rounded-2xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm"
                                >
                                    <option value="asc">Crescente (A-Z)</option>
                                    <option value="desc">Decrescente (Z-A)</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Registros por Página</label>
                                <select
                                    value={recordsPerPage}
                                    onChange={(e) => setRecordsPerPage(Number(e.target.value))}
                                    className="w-full px-4 py-3 border border-red-200 rounded-2xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm"
                                >
                                    <option value={5}>5 registros</option>
                                    <option value={10}>10 registros</option>
                                    <option value={20}>20 registros</option>
                                    <option value={50}>50 registros</option>
                                </select>
                            </div>

                            <div className="flex items-end">
                                <button
                                    onClick={() => loadLogs()}
                                    className="w-full group relative inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-2xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-red-600 via-orange-700 to-red-700 hover:from-red-700 hover:via-orange-800 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-300 transform hover:scale-105"
                                >
                                    <div className="relative z-10 flex items-center">
                                        <MagnifyingGlassIcon className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-200" />
                                        <span>Atualizar</span>
                                    </div>
                                    <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 to-orange-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Cards de Logs - Design Artístico */}
                <div className="grid grid-cols-1 gap-6 mb-8">
                    {states.loading ? (
                        <div className="col-span-full flex items-center justify-center py-12">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                                <p className="mt-4 text-gray-600">Carregando logs...</p>
                            </div>
                        </div>
                    ) : states.dataLogs && states.dataLogs.length > 0 ? (
                        states.dataLogs.map((log) => (
                            <div
                                key={log.id}
                                className="relative bg-gradient-to-br from-red-50 via-white to-orange-50/30 backdrop-blur-sm rounded-3xl shadow-lg border border-red-100/50 hover:shadow-xl transition-all duration-300 transform hover:scale-105 overflow-hidden group"
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-orange-500/5"></div>
                                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-red-400/10 to-orange-400/10 rounded-full -translate-y-12 translate-x-12"></div>

                                <div className="relative p-6">
                                    {/* Header do Card */}
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center">
                                            <div className="p-3 rounded-2xl bg-gradient-to-r from-red-500 via-orange-600 to-red-700 shadow-lg mr-4">
                                                <ExclamationTriangleIcon className="w-6 h-6 text-white" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold bg-gradient-to-r from-gray-900 via-red-800 to-orange-700 bg-clip-text text-transparent">
                                                    Log #{log.id.slice(0, 8)}...
                                                </h3>
                                                <p className="text-sm text-gray-600 mt-1">
                                                    {log.nomeDoArquivo?.split('/').pop() || 'Arquivo não identificado'}
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => openViewModal(log)}
                                            className="p-2 bg-brand-cyan/20 text-brand-cyan-dark rounded-full hover:bg-brand-cyan/30 transition-colors duration-200"
                                            title="Visualizar Detalhes"
                                        >
                                            <EyeIcon className="w-5 h-5" />
                                        </button>
                                    </div>

                                    {/* Informações do Log */}
                                    <div className="mb-4 p-4 bg-gradient-to-r from-gray-50 to-red-50 rounded-2xl border border-gray-100">
                                        <div className="space-y-3">
                                            <div className="flex items-center">
                                                <div className="p-2 rounded-lg bg-red-100 text-red-700 mr-3">
                                                    <DocumentTextIcon className="w-4 h-4" />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-sm font-medium text-gray-700">Descrição do Erro</p>
                                                    <p className="text-sm text-gray-600 truncate">
                                                        {log.descricaoDoErro || 'Sem descrição disponível'}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center">
                                                <div className="p-2 rounded-lg bg-orange-100 text-orange-700 mr-3">
                                                    <CodeBracketIcon className="w-4 h-4" />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-sm font-medium text-gray-700">Linha do Erro</p>
                                                    <p className="text-sm text-gray-600">
                                                        Linha {log.linhaDoErro || 'N/A'}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center">
                                                <div className="p-2 rounded-lg bg-brand-cyan/20 text-brand-cyan-dark mr-3">
                                                    <CalendarIcon className="w-4 h-4" />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-sm font-medium text-gray-700">Data de Criação</p>
                                                    <p className="text-sm text-gray-600">
                                                        {new Date(log.data_criacao).toLocaleString('pt-BR')}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="col-span-full text-center py-12">
                            <div className="p-4 rounded-3xl bg-gradient-to-r from-gray-50 to-red-50 border border-gray-200">
                                <ExclamationTriangleIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum log encontrado</h3>
                                <p className="text-gray-600 mb-4">Não há logs registrados no sistema no momento.</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Paginação */}
                {states.dataLogs && states.dataLogs.length > 0 && (
                    <div className="flex items-center justify-between bg-white rounded-2xl shadow-lg border border-gray-200 p-4">
                        <div className="flex items-center text-sm text-gray-700">
                            <span>Página {states.page + 1} - {states.dataLogs.length} logs</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={previous}
                                disabled={states.page === 0}
                                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronLeftIcon className="w-4 h-4 mr-1" />
                                Anterior
                            </button>
                            <button
                                onClick={next}
                                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Próximo
                                <ChevronRightIcon className="w-4 h-4 ml-1" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal de Visualização */}
            {showViewModal && viewingLog && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="relative bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-3xl">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <div className="p-2 rounded-lg bg-red-100 mr-3">
                                        <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-semibold text-gray-900">
                                            Detalhes do Log
                                        </h2>
                                        <p className="text-sm text-gray-600">
                                            Informações completas do erro
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={closeViewModal}
                                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                                >
                                    <XMarkIcon className="w-6 h-6" />
                                </button>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="space-y-6">
                                {/* Informações Básicas */}
                                <div className="bg-gradient-to-r from-gray-50 to-red-50 rounded-2xl p-6 border border-gray-100">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações Básicas</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">ID do Log</label>
                                            <p className="text-sm text-gray-900 font-mono bg-white p-2 rounded border">
                                                {viewingLog.id}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Linha do Erro</label>
                                            <p className="text-sm text-gray-900 bg-white p-2 rounded border">
                                                {viewingLog.linhaDoErro || 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Descrição do Erro */}
                                <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-6 border border-red-100">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Descrição do Erro</h3>
                                    <div className="bg-white p-4 rounded-lg border">
                                        <p className="text-gray-900 whitespace-pre-wrap">
                                            {viewingLog.descricaoDoErro || 'Sem descrição disponível'}
                                        </p>
                                    </div>
                                </div>

                                {/* Arquivo */}
                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Arquivo</h3>
                                    <div className="bg-white p-4 rounded-lg border">
                                        <p className="text-gray-900 font-mono text-sm break-all">
                                            {viewingLog.nomeDoArquivo || 'Arquivo não identificado'}
                                        </p>
                                    </div>
                                </div>

                                {/* Datas */}
                                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Datas</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Data de Criação</label>
                                            <p className="text-sm text-gray-900 bg-white p-2 rounded border">
                                                {new Date(viewingLog.data_criacao).toLocaleString('pt-BR')}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Data de Atualização</label>
                                            <p className="text-sm text-gray-900 bg-white p-2 rounded border">
                                                {new Date(viewingLog.data_atualizacao).toLocaleString('pt-BR')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-3xl">
                            <div className="flex justify-end">
                                <button
                                    onClick={closeViewModal}
                                    className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                                >
                                    Fechar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )

}

export default Logs;
